import React from 'react';
import { Link, type Href } from 'expo-router';
import { Text, View } from 'react-native';

import { interMedium, interRegular, interSemiBold } from '../../lib/fonts';
import {
    type AgentClientItem,
    type AgentRouteItem,
    type MachineEntry,
} from './agentsPageContent';
import { contentStyles } from './agentsPageContentStyles';
import { styles } from './agentsPageStyles';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import { type LocalizedMessage, resolveMessage } from '../../lib/i18n/messages';

export function ActionLink({
    external = false,
    fontsLoaded,
    href,
    label,
    light = false,
    lightOutline = false,
    narrow,
    primary = false,
}: {
    external?: boolean;
    fontsLoaded: boolean;
    href: Href;
    label: LocalizedMessage;
    light?: boolean;
    lightOutline?: boolean;
    narrow: boolean;
    primary?: boolean;
}) {
    const { i18n } = useLingui();
    return (
        <Link
            href={href}
            target={external ? '_blank' : undefined}
            style={[
                styles.actionLink,
                primary && styles.actionLinkPrimary,
                !primary && !light && !lightOutline && styles.actionLinkSecondary,
                light && styles.actionLinkLight,
                lightOutline && styles.actionLinkOutlineLight,
                narrow && styles.actionLinkNarrow,
                fontsLoaded && interSemiBold,
            ]}
        >
            {resolveMessage(i18n, label)}
        </Link>
    );
}

export function StepCard({
    body,
    fontsLoaded,
    number,
    title,
}: {
    body: LocalizedMessage;
    fontsLoaded: boolean;
    number: string;
    title: LocalizedMessage;
}) {
    const { i18n } = useLingui();
    return (
        <View style={styles.stepCard}>
            <Text style={[styles.stepNumber, fontsLoaded && interMedium]}>{number}</Text>
            <Text style={[styles.cardTitle, fontsLoaded && interSemiBold]}>{resolveMessage(i18n, title)}</Text>
            <Text style={[styles.cardBody, fontsLoaded && interRegular]}>{resolveMessage(i18n, body)}</Text>
        </View>
    );
}

export function RouteCard({
    fontsLoaded,
    narrow,
    ...route
}: AgentRouteItem & { fontsLoaded: boolean; narrow: boolean }) {
    const { i18n } = useLingui();
    return (
        <View style={[contentStyles.routeCard, narrow && contentStyles.routeCardNarrow]}>
            <Text style={[contentStyles.routeLabel, fontsLoaded && interSemiBold]}>{resolveMessage(i18n, route.label)}</Text>
            <Text style={[contentStyles.routeTitle, fontsLoaded && interSemiBold]}>{resolveMessage(i18n, route.title)}</Text>
            <Text style={[contentStyles.routeBody, fontsLoaded && interRegular]}>{resolveMessage(i18n, route.body)}</Text>
            <Text style={contentStyles.routeCode}>{route.code}</Text>
            <Link href={route.href} target="_blank" style={[contentStyles.textLinkLight, fontsLoaded && interSemiBold]}>
                {resolveMessage(i18n, route.linkLabel)} ↗
            </Link>
        </View>
    );
}

export function AgentCard({
    fontsLoaded,
    narrow,
    ...client
}: AgentClientItem & { fontsLoaded: boolean; narrow: boolean }) {
    const { i18n } = useLingui();
    return (
        <View style={[contentStyles.agentCard, narrow && contentStyles.agentCardNarrow]}>
            <View style={contentStyles.agentCardTitleRow}>
                <Text style={[contentStyles.agentCardTitle, fontsLoaded && interSemiBold]}>{resolveMessage(i18n, client.title)}</Text>
                <Text style={[contentStyles.agentCardAccess, fontsLoaded && interMedium]}>{resolveMessage(i18n, client.access)}</Text>
            </View>
            <Text style={[contentStyles.agentCardBody, fontsLoaded && interRegular]}>{resolveMessage(i18n, client.body)}</Text>
            <Link href={client.href} target="_blank" style={[contentStyles.agentCardLink, fontsLoaded && interSemiBold]}><Trans id="agents.agentspagecards.setupGuide" comment="UI copy in components/agents/AgentsPageCards.tsx">
                Setup guide ↗
            </Trans></Link>
        </View>
    );
}

export function UseCaseCard({
    body,
    fontsLoaded,
    index,
    narrow,
    title,
}: {
    body: LocalizedMessage;
    fontsLoaded: boolean;
    index: number;
    narrow: boolean;
    title: LocalizedMessage;
}) {
    const { i18n } = useLingui();
    return (
        <View style={[contentStyles.useCaseCard, narrow && contentStyles.useCaseCardNarrow]}>
            <Text style={[contentStyles.useCaseIndex, fontsLoaded && interMedium]}>
                {String(index).padStart(2, '0')}
            </Text>
            <Text style={[contentStyles.useCaseTitle, fontsLoaded && interSemiBold]}>{resolveMessage(i18n, title)}</Text>
            <Text style={[contentStyles.useCaseBody, fontsLoaded && interRegular]}>{resolveMessage(i18n, body)}</Text>
        </View>
    );
}

export function MachineCard({
    body,
    fontsLoaded,
    href,
    linkLabel,
    secondaryHref,
    secondaryLinkLabel,
    title,
}: MachineEntry & { fontsLoaded: boolean }) {
    const { i18n } = useLingui();
    return (
        <View style={contentStyles.machineCard}>
            <Text style={[contentStyles.machineCardTitle, fontsLoaded && interSemiBold]}>{resolveMessage(i18n, title)}</Text>
            <Text style={[contentStyles.machineCardBody, fontsLoaded && interRegular]}>{resolveMessage(i18n, body)}</Text>
            <View style={contentStyles.machineLinks}>
                <Link href={href} target="_blank" style={[contentStyles.machineLink, fontsLoaded && interSemiBold]}>
                    {resolveMessage(i18n, linkLabel)} ↗
                </Link>
                {secondaryHref && secondaryLinkLabel && (
                    <Link
                        href={secondaryHref}
                        target="_blank"
                        style={[contentStyles.machineLink, fontsLoaded && interSemiBold]}
                    >
                        {resolveMessage(i18n, secondaryLinkLabel)} ↗
                    </Link>
                )}
            </View>
        </View>
    );
}
